import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Form, Button, Alert, Spinner, Table, Badge, Tab, Tabs } from 'react-bootstrap';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { API_BASE_URL, ENDPOINTS, DEFAULT_STORE_ID } from '../config';
import { useAuth } from '../contexts/AuthContext';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import './ProductRequest.css';

const ProductRequest: React.FC = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const storeId = parseInt(searchParams.get('storeId') || DEFAULT_STORE_ID.toString());
  const { user, isAuthenticated, token } = useAuth();

  const [formData, setFormData] = useState({
    productName: '',
    specifications: '',
    quantity: '',
    notes: '',
    requestType: '1' // 1 = Order Product, 2 = Supply Request
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const [requests, setRequests] = useState<any[]>([]);
  const [loadingRequests, setLoadingRequests] = useState(false);
  const [activeTab, setActiveTab] = useState('new');

  useEffect(() => {
    if (isAuthenticated && token) {
      fetchRequests();
    }
  }, [isAuthenticated, token, storeId]);

  const fetchRequests = async () => {
    try {
      setLoadingRequests(true);
      const response = await fetch(`${API_BASE_URL}${ENDPOINTS.STORE_PRODUCT_REQUESTS(storeId)}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept-Language': i18n.language
        }
      });
      if (response.ok) {
        const data = await response.json();
        setRequests(data);
      }
    } catch (error) {
      console.error('Error fetching requests:', error);
    } finally {
      setLoadingRequests(false);
    }
  };

  // Load saved request from localStorage on mount and check URL params
  useEffect(() => {
    const source = searchParams.get('source'); // 'China' or 'Gulf'
    const savedRequest = localStorage.getItem('pendingProductRequest');
    
    if (savedRequest) {
      try {
        const parsed = JSON.parse(savedRequest);
        setFormData(parsed);
      } catch (e) {
        console.error('Error loading saved request:', e);
      }
    } else if (source) {
      // If source is specified in URL, add it to notes
      const sourceText = source === 'China' ? t('sourceChina') : t('sourceGulf');
      setFormData(prev => ({
        ...prev,
        notes: `${sourceText}${prev.notes ? '\n' + prev.notes : ''}`
      }));
    }
  }, [searchParams, t]);

  // Auto-submit saved request after authentication
  useEffect(() => {
    const savedRequest = localStorage.getItem('pendingProductRequest');
    if (isAuthenticated && user && savedRequest) {
      const submitSavedRequest = async () => {
        try {
          const parsed = JSON.parse(savedRequest);
          setFormData(parsed);
          
          const requestBody = {
            customerId: user.id,
            productName: parsed.productName.trim(),
            specifications: parsed.specifications.trim() || null,
            requestedQuantity: parseFloat(parsed.quantity),
            notes: parsed.notes.trim() || null,
            requestType: parseInt(parsed.requestType)
          };

          const token = localStorage.getItem('authToken');
          if (!token) {
            setError(t('sessionExpiredMsg') as string);
            setTimeout(() => {
              navigate('/login?redirect=/product-request');
            }, 2000);
            return;
          }

          setLoading(true);
          const response = await fetch(`${API_BASE_URL}${ENDPOINTS.CREATE_PRODUCT_REQUEST(storeId)}`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify(requestBody)
          });

          if (response.status === 401) {
            localStorage.removeItem('authToken');
            setError(t('sessionExpiredMsg') as string);
            setTimeout(() => {
              navigate('/login?redirect=/product-request');
            }, 2000);
            return;
          }

          if (response.ok) {
            localStorage.removeItem('pendingProductRequest');
            setSuccess(true);
            setFormData({
              productName: '',
              specifications: '',
              quantity: '',
              notes: '',
              requestType: '1'
            });
            // Refresh list
            fetchRequests();
          } else {
            setError(t('savedRequestError') as string);
          }
        } catch (err: any) {
          setError(t('savedRequestErrorGeneric') as string);
        } finally {
          setLoading(false);
        }
      };

      submitSavedRequest();
    }
  }, [isAuthenticated, user, storeId, navigate, t]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.productName.trim()) {
      setError(t('enterProductName') as string);
      return;
    }

    if (!formData.quantity || parseFloat(formData.quantity) <= 0) {
      setError(t('enterValidQuantity') as string);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      setSuccess(false);

      // Check if user is authenticated
      if (!isAuthenticated || !user || !token) {
        // Save request to localStorage
        localStorage.setItem('pendingProductRequest', JSON.stringify(formData));
        // Redirect to login with message
        navigate(`/login?redirect=/product-request&message=${encodeURIComponent(t('loginToContinueRequest') as string)}`);
        return;
      }

      const requestBody = {
        customerId: user.id,
        productName: formData.productName.trim(),
        specifications: formData.specifications.trim() || null,
        requestedQuantity: parseFloat(formData.quantity),
        notes: formData.notes.trim() || null,
        requestType: parseInt(formData.requestType)
      };

      const url = `${API_BASE_URL}${ENDPOINTS.CREATE_PRODUCT_REQUEST(storeId)}`;
      
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(requestBody)
      });
      const responseForErrorText = response.clone();
      
      if (response.status === 401) {
        // Unauthorized - token expired or invalid
        localStorage.removeItem('authToken');
        localStorage.setItem('pendingProductRequest', JSON.stringify(formData));
        setError(t('sessionExpiredMsg') as string);
        setTimeout(() => {
          navigate(`/login?redirect=/product-request&message=${encodeURIComponent(t('sessionExpired') as string)}`);
        }, 2000);
        return;
      }

      if (!response.ok) {
        let errorMessage = t('requestFailed') as string;
        try {
          const errorData = await response.json();
          if (errorData.errors) {
            const validationErrors = Object.values(errorData.errors).flat().join(', ');
            errorMessage = `${t('validationErrors')}: ${validationErrors}`;
          } else if (errorData.message) {
            errorMessage = errorData.message;
          }
        } catch (parseError) {
          const errorText = await responseForErrorText.text();
          errorMessage = errorText || `${t('errorPrefix')} ${response.status}: ${response.statusText}`;
        }
        throw new Error(errorMessage);
      }

      setSuccess(true);
      // Remove saved request if exists
      localStorage.removeItem('pendingProductRequest');
      // Reset form
      setFormData({
        productName: '',
        specifications: '',
        quantity: '',
        notes: '',
        requestType: '1'
      });

      // Refresh requests list
      fetchRequests();

      // Clear success message after 5 seconds
      setTimeout(() => {
        setSuccess(false);
      }, 5000);

    } catch (err: any) {
      setError(err.message || t('errorSendingRequest') as string);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="product-request-page bg-light min-vh-100">
      <Helmet>
        <title>{t('requestNewProduct')} - MIC Green Store</title>
        <meta name="description" content={t('requestNewProductDesc') as string} />
      </Helmet>

      <Navbar />

      <Container className="py-5">
        <Card className="border-0 shadow-sm rounded-3">
            <Card.Header className="bg-white p-0 border-bottom">
                <Tabs
                    activeKey={activeTab}
                    onSelect={(k) => setActiveTab(k || 'new')}
                    className="profile-tabs border-0"
                    fill
                >
                    <Tab eventKey="new" title={
                        <div className="py-3">
                            <i className="fas fa-plus-circle me-2"></i>
                            {t('requestNewProduct')}
                        </div>
                    }>
                    </Tab>
                    {isAuthenticated && (
                        <Tab eventKey="history" title={
                            <div className="py-3">
                                <i className="fas fa-history me-2"></i>
                                {t('previousRequests')}
                            </div>
                        }>
                        </Tab>
                    )}
                </Tabs>
            </Card.Header>
            <Card.Body className="p-4">
                {activeTab === 'new' && (
                    <Row className="justify-content-center">
                        <Col md={8} lg={6}>
                            {success ? (
                                <Alert variant="success" className="text-center mb-0">
                                <div className="mb-2">
                                    <i className="fas fa-check-circle fa-3x text-success"></i>
                                </div>
                                <Alert.Heading className="h5">{t('requestSentSuccess')}</Alert.Heading>
                                <p className="small mb-0">{t('requestSentDesc')}</p>
                                </Alert>
                            ) : (
                                <>
                                {error && (
                                    <Alert variant="danger" dismissible onClose={() => setError(null)} className="small">
                                    <i className="fas fa-exclamation-circle me-2"></i>
                                    {error}
                                    </Alert>
                                )}

                                <Form onSubmit={handleSubmit}>
                                    <Form.Group className="mb-3">
                                    <Form.Label className="small fw-bold text-muted mb-1">
                                        {t('requestType')} <span className="text-danger">*</span>
                                    </Form.Label>
                                    <Form.Select
                                        name="requestType"
                                        value={formData.requestType}
                                        onChange={handleInputChange}
                                        required
                                        className="form-select-sm"
                                    >
                                        <option value="1">{t('supplyRequest')}</option>
                                        <option value="2">{t('shippingRequest')}</option>
                                    </Form.Select>
                                    </Form.Group>

                                    <Form.Group className="mb-3">
                                    <Form.Label className="small fw-bold text-muted mb-1">
                                        {t('itemName')} <span className="text-danger">*</span>
                                    </Form.Label>
                                    <Form.Control
                                        type="text"
                                        name="productName"
                                        value={formData.productName}
                                        onChange={handleInputChange}
                                        placeholder={t('productNamePlaceholder') as string}
                                        required
                                        className="form-control-sm"
                                    />
                                    </Form.Group>

                                    <Form.Group className="mb-3">
                                    <Form.Label className="small fw-bold text-muted mb-1">{t('specifications')}</Form.Label>
                                    <Form.Control
                                        as="textarea"
                                        rows={3}
                                        name="specifications"
                                        value={formData.specifications}
                                        onChange={handleInputChange}
                                        placeholder={t('specificationsPlaceholder') as string}
                                        className="form-control-sm"
                                    />
                                    </Form.Group>

                                    <Form.Group className="mb-3">
                                    <Form.Label className="small fw-bold text-muted mb-1">
                                        {t('requestedQuantity')} <span className="text-danger">*</span>
                                    </Form.Label>
                                    <Form.Control
                                        type="number"
                                        name="quantity"
                                        value={formData.quantity}
                                        onChange={handleInputChange}
                                        placeholder="0"
                                        min="0.01"
                                        step="0.01"
                                        required
                                        className="form-control-sm"
                                    />
                                    </Form.Group>

                                    <Form.Group className="mb-3">
                                    <Form.Label className="small fw-bold text-muted mb-1">{t('additionalNotes')}</Form.Label>
                                    <Form.Control
                                        as="textarea"
                                        rows={2}
                                        name="notes"
                                        value={formData.notes}
                                        onChange={handleInputChange}
                                        placeholder={t('additionalNotesPlaceholder') as string}
                                        className="form-control-sm"
                                    />
                                    </Form.Group>

                                    <div className="d-grid gap-2">
                                    <Button
                                        type="submit"
                                        variant="primary"
                                        disabled={loading}
                                        className="btn-sm fw-bold py-2"
                                    >
                                        {loading ? (
                                        <>
                                            <Spinner
                                            as="span"
                                            animation="border"
                                            size="sm"
                                            role="status"
                                            aria-hidden="true"
                                            className="me-2"
                                            />
                                            {t('sending')}
                                        </>
                                        ) : (
                                        <>
                                            <i className="fas fa-paper-plane me-2"></i>
                                            {t('submitRequest')}
                                        </>
                                        )}
                                    </Button>
                                    </div>
                                </Form>
                                </>
                            )}
                        </Col>
                    </Row>
                )}

                {activeTab === 'history' && isAuthenticated && (
                    <div className="history-section">
                        {loadingRequests ? (
                            <div className="text-center py-5">
                            <Spinner animation="border" variant="primary" />
                            </div>
                        ) : requests.length === 0 ? (
                            <div className="text-center py-5 text-muted">
                            <div className="mb-3">
                                <i className="fas fa-inbox fa-3x text-light"></i>
                            </div>
                            <p>{t('noRequestsFound')}</p>
                            </div>
                        ) : (
                            <div className="table-responsive">
                            <Table hover className="mb-0 align-middle text-nowrap">
                                <thead className="bg-light">
                                <tr>
                                    <th className="border-0 py-3 ps-4">#</th>
                                    <th className="border-0 py-3">{t('requestDate')}</th>
                                    <th className="border-0 py-3">{t('itemName')}</th>
                                    <th className="border-0 py-3">{t('quantity')}</th>
                                    <th className="border-0 py-3">{t('status')}</th>
                                </tr>
                                </thead>
                                <tbody>
                                {requests.map((req, index) => (
                                    <tr key={req.requestId}>
                                    <td className="ps-4 text-muted small">{index + 1}</td>
                                    <td className="small">{new Date(req.requestDate).toLocaleDateString()}</td>
                                    <td className="fw-bold text-dark">{req.productName}</td>
                                    <td>{req.requestedQuantity}</td>
                                    <td>
                                        <Badge bg={
                                        req.requestStatus === 1 ? 'warning' :
                                        req.requestStatus === 2 ? 'info' :
                                        req.requestStatus === 3 ? 'danger' :
                                        'success'
                                        } className="fw-normal px-3 py-2 rounded-pill">
                                        {req.requestStatusName}
                                        </Badge>
                                    </td>
                                    </tr>
                                ))}
                                </tbody>
                            </Table>
                            </div>
                        )}
                    </div>
                )}
            </Card.Body>
        </Card>
      </Container>

      <Footer />
    </div>
  );
};

export default ProductRequest;
